// Brand motifs for the public pages: owl, nest, wing, letter. All decorative –
// keep them aria-hidden, non-interactive, and subtle. Everything else on the
// public surface should use lucide-react so meaning stays instantly clear.

import { forwardRef } from 'react'

/** The wing glyph that flies across the hero's rotating word. */
export const WingMark = forwardRef(function WingMark({ className }, ref) {
  return (
    <svg ref={ref} className={className} viewBox="0 0 48 22" fill="currentColor" aria-hidden="true">
      <path d="M2 7Q13 1.5 24 11Q35 1.5 46 7Q35 7.5 24 15Q13 7.5 2 7Z" />
    </svg>
  )
})

/** A dotted flight path ending in the brand wing – a quiet section ornament. */
export function FlightPathMark({ className }) {
  return (
    <svg className={className} viewBox="0 0 220 40" fill="none" aria-hidden="true">
      <path
        d="M4 34C56 31 122 25 188 11"
        stroke="currentColor"
        strokeWidth="1.4"
        strokeDasharray="1 6"
        strokeLinecap="round"
      />
      <g transform="translate(186 3) scale(0.55)" fill="currentColor">
        <path d="M2 7Q13 1.5 24 11Q35 1.5 46 7Q35 7.5 24 15Q13 7.5 2 7Z" />
      </g>
    </svg>
  )
}

/** Julio holding a letter, for the contact page hero – same filled avatar as JulioAvatar,
    with wings tucked around an envelope. "Write to us" delivered by the assistant himself. */
export function OwlWithLetterMark({ className }) {
  return (
    <svg className={className} viewBox="0 0 64 64" aria-hidden="true">
      <g transform="translate(12 0) scale(0.8333)">
        <circle cx="24" cy="24" r="24" fill="#f3e7d3" />
        <path d="M14.5 13c.8-3.6 3-5.8 5.8-6.8-1.2 2.2-1.6 4.6-1.3 6.6z" fill="#6f5d4b" />
        <path d="M33.5 13c-.8-3.6-3-5.8-5.8-6.8 1.2 2.2 1.6 4.6 1.3 6.6z" fill="#6f5d4b" />
        <path d="M24 8c9 0 15 6.6 15 16.2C39 34 32.6 41 24 41S9 34 9 24.2C9 14.6 15 8 24 8z" fill="#8a7360" />
        <ellipse cx="24" cy="34.4" rx="8" ry="7" fill="#f6eddd" />
        <circle cx="17.6" cy="19.6" r="7.5" fill="#f6eddd" />
        <circle cx="30.4" cy="19.6" r="7.5" fill="#f6eddd" />
        <circle cx="17.9" cy="19.9" r="4.5" fill="#ffffff" />
        <circle cx="30.1" cy="19.9" r="4.5" fill="#ffffff" />
        <circle cx="18.3" cy="20.1" r="3.1" fill="#d99a3d" />
        <circle cx="29.7" cy="20.1" r="3.1" fill="#d99a3d" />
        <circle cx="18.5" cy="20.3" r="1.8" fill="#3a2f24" />
        <circle cx="29.5" cy="20.3" r="1.8" fill="#3a2f24" />
        <circle cx="19.3" cy="19.3" r=".9" fill="#ffffff" />
        <circle cx="30.3" cy="19.3" r=".9" fill="#ffffff" />
        <path
          d="M24 22.8l-2.3 2.5c-.5.6-.4 1.4.2 1.9l1.4 1.1c.4.3 1 .3 1.4 0l1.4-1.1c.6-.5.7-1.3.2-1.9z"
          fill="#e6a344"
        />
      </g>
      {/* wings tucked around the envelope */}
      <path d="M22 46c-4 2.6-6.6 6.3-5.2 10.4 3.5-1.3 6.2-4 7.6-7.8z" fill="#8a7360" />
      <path d="M42 46c4 2.6 6.6 6.3 5.2 10.4-3.5-1.3-6.2-4-7.6-7.8z" fill="#8a7360" />
      {/* envelope */}
      <rect x="20" y="46" width="24" height="15" rx="1" fill="#f6eddd" stroke="#6f5d4b" strokeWidth="1.8" strokeLinejoin="round" />
      <path
        d="M20 46.5l12 8.5 12-8.5"
        fill="none"
        stroke="#6f5d4b"
        strokeWidth="1.8"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  )
}

/** Julio, the assistant owl – filled avatar used wherever Julio speaks. */
export function JulioAvatar({ className }) {
  return (
    <svg className={className} viewBox="0 0 48 48" aria-hidden="true">
      <circle cx="24" cy="24" r="24" fill="#f3e7d3" />
      <path d="M14.5 13c.8-3.6 3-5.8 5.8-6.8-1.2 2.2-1.6 4.6-1.3 6.6z" fill="#6f5d4b" />
      <path d="M33.5 13c-.8-3.6-3-5.8-5.8-6.8 1.2 2.2 1.6 4.6 1.3 6.6z" fill="#6f5d4b" />
      <path d="M24 8c9 0 15 6.6 15 16.2C39 34 32.6 41 24 41S9 34 9 24.2C9 14.6 15 8 24 8z" fill="#8a7360" />
      <path d="M11.2 24c-1.4 4.6-.4 9.2 2.6 12.4.9-4.2.5-8.6-.9-12.6z" fill="#6f5d4b" />
      <path d="M36.8 24c1.4 4.6.4 9.2-2.6 12.4-.9-4.2-.5-8.6.9-12.6z" fill="#6f5d4b" />
      <ellipse cx="24" cy="34.4" rx="8" ry="7" fill="#f6eddd" />
      <g fill="none" stroke="#c9b393" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round">
        <path d="M21.2 31.6l1.6 1.5 1.6-1.5" />
        <path d="M25.6 34.4l1.6 1.5 1.6-1.5" />
        <path d="M19.2 35.4l1.6 1.5 1.6-1.5" />
      </g>
      <circle cx="17.6" cy="19.6" r="7.5" fill="#f6eddd" />
      <circle cx="30.4" cy="19.6" r="7.5" fill="#f6eddd" />
      <circle cx="17.9" cy="19.9" r="4.5" fill="#ffffff" />
      <circle cx="30.1" cy="19.9" r="4.5" fill="#ffffff" />
      <circle cx="18.3" cy="20.1" r="3.1" fill="#d99a3d" />
      <circle cx="29.7" cy="20.1" r="3.1" fill="#d99a3d" />
      <circle cx="18.5" cy="20.3" r="1.8" fill="#3a2f24" />
      <circle cx="29.5" cy="20.3" r="1.8" fill="#3a2f24" />
      <circle cx="19.3" cy="19.3" r=".9" fill="#ffffff" />
      <circle cx="30.3" cy="19.3" r=".9" fill="#ffffff" />
      <path
        d="M24 22.8l-2.3 2.5c-.5.6-.4 1.4.2 1.9l1.4 1.1c.4.3 1 .3 1.4 0l1.4-1.1c.6-.5.7-1.3.2-1.9z"
        fill="#e6a344"
      />
    </svg>
  )
}
